import { defineStore } from "pinia";
import { useRuntimeConfig } from "#app";
import { ref, computed, readonly } from "vue";
import { useUserStore } from "@/store/user";
import { useOrgStore } from "@/store/org";
import { useFeatureManagement } from "@/composables/useFeatureManagement";
import type { IntercomOptions } from "@/types/IntercomOptions";
import { useCookieConsentStore } from "@/store/cookieConsentStore";
import { useIntercomScriptWrapper } from "@/utils/scripts";
import { clearAllStorageByPattern } from "@/utils/cookieUtils";
import { TRACKING_VENDOR_STORAGE_PATTERNS } from "@/constants/trackingVendorStoragePatterns";

export const useIntercomStore = defineStore("intercom", () => {
  // Internal script state
  let _script: any = null;

  const getIntercomScript = () => {
    const config = useRuntimeConfig();
    if (import.meta.server) return null;
    if (!_script) {
      _script = useIntercomScriptWrapper({
        app_id: config.public.INTERCOM_APP_ID as string,
        api_base: config.public.INTERCOM_APP_URL as string,
        scriptOptions: {
          trigger: "manual",
        },
      });
      // Register listeners once when script is first initialized
      _script.onLoaded(() => {
        initializeListeners();
      });
    }
    return _script;
  };

  const isIntercomVisible = ref(false);
  const isBooted = ref(false);
  const isIntercomAvailableState = ref(false);

  // Checks if Intercom is available (script loaded)
  const isIntercomAvailable = computed(() => {
    if (isIntercomAvailableState.value) return true;
    const script = getIntercomScript();
    return script?.status.value === "loaded";
  });

  const setScriptLoaded = (available: boolean) => {
    isIntercomAvailableState.value = available;
  };

  /**
   * For backward compatibility with legacy components
   */
  const checkIntercomAvailability = () => {
    const config = useRuntimeConfig();
    if (import.meta.server) return false;
    const isAvailable = Boolean(
      (window as any).Intercom && String(config.public.IS_PROD) === "true",
    );
    isIntercomAvailableState.value = isAvailable;
    return isAvailable;
  };

  const setIntercomVisibility = (visible: boolean) => {
    isIntercomVisible.value = visible;
  };

  const getIntercomClient = (script: any) => {
    const proxyIntercom = script?.proxy?.Intercom;
    if (typeof proxyIntercom === "function") return proxyIntercom;

    const instanceIntercom = script?.instance?.Intercom;
    if (typeof instanceIntercom === "function") return instanceIntercom;

    const directIntercom = script?.Intercom;
    if (typeof directIntercom === "function") return directIntercom;

    const globalIntercom = (window as any).Intercom;
    if (typeof globalIntercom === "function") return globalIntercom;

    return null;
  };

  // Initializes Intercom event listeners
  const initializeListeners = () => {
    const script = getIntercomScript();
    if (!script || script.status.value !== "loaded") return;

    const intercom = getIntercomClient(script);
    if (!intercom) return;

    intercom("onHide", () => {
      setIntercomVisibility(false);
    });
    intercom("onShow", () => {
      setIntercomVisibility(true);
    });

    // Mark as available once listeners are attached
    setScriptLoaded(true);
  };

  const resetBootState = () => {
    isBooted.value = false;
  };

  const bootIntercom = async (options: { anonymous?: boolean } = {}) => {
    const config = useRuntimeConfig();
    const isProd = String(config.public.IS_PROD) === "true";
    if (!isProd) return;

    const userStore = useUserStore();
    if (userStore.isUserImpersonated) {
      return;
    }

    const orgStore = useOrgStore();

    const user = userStore.getUser;
    const org = orgStore.getOrg;

    if (!user?.id || !org?.id || !org?.attributes) {
      return;
    }

    const script = getIntercomScript();
    if (!script) return;

    // Ensure script is loaded before booting
    if (script.status.value !== "loaded") {
      await script.load();
    }

    // Re-check consent after async load — consent may have been revoked while awaiting
    const cookieConsentStore = useCookieConsentStore();
    if (!cookieConsentStore.areFunctionalCookiesEnabled) return;

    const attrs = org.attributes;
    const { getDigitalLicenses, getPrintLicenses, getLicenseRegion } =
      useFeatureManagement();
    const digitalLicenses = getDigitalLicenses();
    const printLicenses = getPrintLicenses();
    const licenseRegion = getLicenseRegion();

    const identityProps =
      !options.anonymous && user.intercom_hash
        ? {
            user_id: `360-${user.id}`,
            user_hash: user.intercom_hash,
            email: user.email,
          }
        : {};

    const intercomOptions: IntercomOptions = {
      api_base: config.public.INTERCOM_APP_URL as string,
      app_id: config.public.INTERCOM_APP_ID as string,
      hide_default_launcher: false,
      browserLanguage: navigator.language,

      name: user.name,
      ...identityProps,

      userSignUpDate: user.created_at
        ? new Date(user.created_at).toISOString()
        : undefined,

      orgName: attrs.name,
      orgId: org.id,
      master_customer_number: attrs.master_customer_number ?? "1234567890",
      planName: attrs.plan_name,
      orgSignUpDate: attrs.created_at
        ? new Date(attrs.created_at).toISOString()
        : undefined,
      orgEndDate: attrs.end_date
        ? new Date(attrs.end_date).toISOString()
        : undefined,
      trialAccount: attrs.trial_account,
      digitalLicenses,
      printLicenses,
      licenseRegion: licenseRegion || "",

      company: {
        id: attrs.master_customer_number ?? "1234567890",
        name: attrs.name,
        client_org_id: org.id,
        endDate: attrs.end_date
          ? new Date(attrs.end_date).toISOString()
          : undefined,
        trialAccount: attrs.trial_account,
        planName: attrs.plan_name,
        timeZone: attrs.time_zone,
        digitalLicenses,
        printLicenses,
        licenseRegion: licenseRegion || "",
        subscriptionId:
          attrs.relationships?.subscriptions?.data?.[0]?.id || null,
        internalAccount: attrs.internal_account || false,
      },
    };

    try {
      const intercom = getIntercomClient(script);
      if (!intercom) return;

      if (!isBooted.value) {
        intercom("boot", intercomOptions as any);
        isBooted.value = true;
      } else {
        intercom("update", intercomOptions as any);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to boot/update Intercom:", error);
    }
  };

  const shutdownIntercom = () => {
    const script = getIntercomScript();
    try {
      if (script && script.status.value === "loaded") {
        const intercom = getIntercomClient(script);
        if (intercom) {
          intercom("update", { hide_default_launcher: true });
          intercom("shutdown");
        }
      }
    } finally {
      resetBootState();
      setScriptLoaded(false);
      clearAllStorageByPattern(TRACKING_VENDOR_STORAGE_PATTERNS.intercom);
    }
  };

  // Shows the Intercom widget
  const showIntercom = () => {
    const cookieConsentStore = useCookieConsentStore();
    if (!cookieConsentStore.areFunctionalCookiesEnabled) {
      cookieConsentStore.isReadyToChatDialogVisible = true;
      return;
    }

    const userStore = useUserStore();
    if (userStore.isUserImpersonated) {
      return;
    }

    const intercom = getIntercomClient(getIntercomScript());
    if (intercom) {
      intercom("show");
      setIntercomVisibility(true);
    }
  };

  // Hides the Intercom widget
  const hideIntercom = () => {
    const intercom = getIntercomClient(getIntercomScript());
    if (intercom) {
      intercom("hide");
      setIntercomVisibility(false);
    }
  };

  // Toggles the visibility of the Intercom widget
  const toggleIntercom = () => {
    const cookieConsentStore = useCookieConsentStore();
    if (!cookieConsentStore.areFunctionalCookiesEnabled) {
      cookieConsentStore.isReadyToChatDialogVisible = true;
      return;
    }

    const userStore = useUserStore();
    if (userStore.isUserImpersonated) {
      return;
    }

    const ic = (window as any).Intercom;
    if (typeof ic === "function") {
      ic("reattach_activator");
      if (isIntercomVisible.value) {
        ic("hide");
        setIntercomVisibility(false);
      } else {
        ic("show");
        setIntercomVisibility(true);
      }
    } else if (isIntercomVisible.value) {
      // Fallback if global not yet initialized
      hideIntercom();
    } else {
      showIntercom();
    }
  };

  return {
    isIntercomVisible: readonly(isIntercomVisible),
    isIntercomAvailable,
    checkIntercomAvailability,
    bootIntercom,
    resetBootState,
    shutdownIntercom,
    showIntercom,
    hideIntercom,
    toggleIntercom,
  };
});
